import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Icon } from "../components/Icon";
import { useSandboxDock } from "../components/SandboxDock";
import { api } from "../lib/api";
import { copyText } from "../lib/clipboard";
import { renderDiagrams } from "../lib/mermaid";
import { useSession } from "../lib/session";
import { useTheme } from "../lib/theme";
import { useToast } from "../lib/toast";
import { formatMinutes } from "../lib/format";
import { useModuleCtx } from "./ModuleShell";
import NotFound from "./NotFound";

interface Heading {
  id: string;
  text: string;
  level: 2 | 3;
}

export default function LessonReader() {
  const { lessonId = "" } = useParams();
  const { content, progress, reloadModuleProgress } = useModuleCtx();
  const { setProgress } = useSession();
  const { resolved } = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  const dock = useSandboxDock();

  const proseRef = useRef<HTMLDivElement | null>(null);
  const [headings, setHeadings] = useState<Heading[]>([]);
  const [activeHeading, setActiveHeading] = useState<string | null>(null);
  const [marking, setMarking] = useState(false);

  const index = content.lessons.findIndex((lesson) => lesson.id === lessonId);
  const lesson = index >= 0 ? content.lessons[index] : null;
  const base = `/modules/${content.module.id}`;
  const done = !!progress.lessons[lessonId];

  const previous = index > 0 ? content.lessons[index - 1] : null;
  const nextLesson = index >= 0 && index < content.lessons.length - 1
    ? content.lessons[index + 1]
    : null;

  // ---------------------------------------------------------------- enhance the rendered HTML
  useEffect(() => {
    const container = proseRef.current;
    if (!container || !lesson) return;

    // Table of contents from the ids the content build puts on headings.
    const found: Heading[] = Array.from(container.querySelectorAll<HTMLElement>("h2[id], h3[id]"))
      .map((element) => ({
        id: element.id,
        text: element.textContent?.trim() ?? element.id,
        level: element.tagName === "H3" ? 3 : 2,
      }));
    setHeadings(found);
    setActiveHeading(found[0]?.id ?? null);

    // Working copy buttons on every code figure.
    const buttons = Array.from(container.querySelectorAll<HTMLButtonElement>("button.copy-code"));
    const timers: Array<ReturnType<typeof setTimeout>> = [];
    const onCopy = async (event: Event) => {
      const button = event.currentTarget as HTMLButtonElement;
      const code = button.dataset.code ?? button.closest("figure")?.querySelector("code")?.textContent ?? "";
      const ok = await copyText(code);
      button.dataset.copied = ok ? "true" : "false";
      button.textContent = ok ? "Copied" : "Press Ctrl+C";
      timers.push(setTimeout(() => {
        button.removeAttribute("data-copied");
        button.textContent = "Copy";
      }, 1800));
    };
    for (const button of buttons) {
      button.type = "button";
      if (!button.getAttribute("aria-label")) {
        const caption = button.closest("figure")?.querySelector("figcaption")?.textContent?.trim();
        button.setAttribute("aria-label", caption ? `Copy the ${caption} snippet` : "Copy this snippet");
      }
      button.addEventListener("click", onCopy);
    }

    // Headings get scroll anchors; the observer drives the contents list.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        if (visible?.target instanceof HTMLElement) setActiveHeading(visible.target.id);
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: [0, 1] },
    );
    for (const element of container.querySelectorAll("h2[id], h3[id]")) observer.observe(element);

    // "Try it in your sandbox": a button under the heading that opens the docked terminal.
    const tryButtons: HTMLButtonElement[] = [];
    const openDock = () => dock.open();
    if (dock.available) {
      for (const heading of container.querySelectorAll<HTMLElement>("h2, h3")) {
        if (!/try it in your sandbox/i.test(heading.textContent ?? "")) continue;
        const button = document.createElement("button");
        button.type = "button";
        button.className = "btn btn-sm try-sandbox";
        button.textContent = "Open the sandbox";
        button.addEventListener("click", openDock);
        heading.insertAdjacentElement("afterend", button);
        tryButtons.push(button);
      }
    }

    return () => {
      observer.disconnect();
      for (const button of buttons) button.removeEventListener("click", onCopy);
      for (const timer of timers) clearTimeout(timer);
      for (const button of tryButtons) {
        button.removeEventListener("click", openDock);
        button.remove();
      }
    };
  }, [lesson, dock]);

  // Diagrams are drawn after the HTML lands, and redrawn when the theme changes.
  useEffect(() => {
    const container = proseRef.current;
    if (!container || !lesson) return;
    void renderDiagrams(container, resolved);
  }, [lesson, resolved]);

  // A new lesson starts at the top of the page.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [lessonId]);

  const markComplete = useCallback(async () => {
    if (!lesson) return;
    setMarking(true);
    try {
      const result = await api.completeLesson(lesson.id);
      setProgress(result.progress);
      await reloadModuleProgress();
      if (nextLesson) navigate(`${base}/lessons/${nextLesson.id}`);
      else navigate(`${base}/quiz`);
    } catch (error) {
      toast.reportError(error, "That lesson could not be marked complete.");
    } finally {
      setMarking(false);
    }
  }, [lesson, nextLesson, base, navigate, reloadModuleProgress, setProgress, toast]);

  const html = useMemo(() => ({ __html: lesson?.html ?? "" }), [lesson]);

  if (!lesson) return <NotFound />;

  return (
    <div className="reader">
      <header className="reader-head">
        <h1>{lesson.title}</h1>
        <div className="reader-meta">
          <span>
            Lesson {index + 1} of {content.lessons.length}
          </span>
          <span>{formatMinutes(lesson.minutes)}</span>
          {done ? (
            <span className="mark mark-done">
              <Icon name="check" size={13} className="glyph" />
              Complete
            </span>
          ) : null}
        </div>
      </header>

      {lesson.objectives.length > 0 ? (
        <section className="objectives" aria-labelledby={`objectives-${lesson.id}`}>
          <h2 id={`objectives-${lesson.id}`}>In this lesson</h2>
          <ul>
            {lesson.objectives.map((objective) => (
              <li key={objective}>{objective}</li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="reader-layout">
        {/* The HTML is compiled and sanitised by our own content build at deploy time. */}
        <div className="prose" ref={proseRef} key={lesson.id} dangerouslySetInnerHTML={html} />

        {headings.length > 1 ? (
          <nav className="toc" aria-label="On this page">
            <h2>On this page</h2>
            <ol>
              {headings.map((heading) => (
                <li key={heading.id} className={heading.level === 3 ? "toc-3" : undefined}>
                  <a
                    href={`#${heading.id}`}
                    className={activeHeading === heading.id ? "is-active" : undefined}
                    aria-current={activeHeading === heading.id ? "location" : undefined}
                  >
                    {heading.text}
                  </a>
                </li>
              ))}
            </ol>
          </nav>
        ) : null}
      </div>

      <div className="lesson-foot">
        <button
          type="button"
          className={done ? "btn" : "btn btn-primary"}
          onClick={() => void markComplete()}
          disabled={marking}
        >
          {marking ? <span className="spinner" /> : <Icon name="check" />}
          {done
            ? nextLesson ? "Next lesson" : "On to the quiz"
            : nextLesson ? "Mark as complete and continue" : "Mark as complete and take the quiz"}
        </button>

        <div className="pager">
          {previous ? (
            <Link className="btn" to={`${base}/lessons/${previous.id}`}>
              <Icon name="arrowLeft" />
              {previous.title}
            </Link>
          ) : (
            <Link className="btn" to={base}>
              <Icon name="arrowLeft" />
              Module overview
            </Link>
          )}
          {nextLesson ? (
            <Link className="btn" to={`${base}/lessons/${nextLesson.id}`}>
              {nextLesson.title}
              <Icon name="arrowRight" />
            </Link>
          ) : (
            <Link className="btn" to={`${base}/quiz`}>
              Quiz
              <Icon name="arrowRight" />
            </Link>
          )}
        </div>
      </div>

      <p className="hint lesson-report">
        Something wrong or unclear in this lesson?{" "}
        <Link to={`/feedback?kind=content&page=${encodeURIComponent(`${base}/lessons/${lesson.id}`)}`}>
          Report a problem with this lesson
        </Link>
      </p>
    </div>
  );
}
